"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.filterFinleyValleyBankTapes = exports.filterCuoMaintAndUpdateVanCmmt = exports.filterCuoLoan = exports.filterVanLoanAcct = exports.filterVanCmmt = exports.filterPsPlusSecDetail = exports.filterPsPlusAccount = void 0;
/* eslint-disable camelcase */
const common_1 = require("@finley-technologies/common");
const consts_1 = require("../../consts");
const isPrivateBankingCostCenter = (costCenter) => consts_1.VALLEY_BANK_PRIVATE_BANKING_COST_CENTER_SET.has(costCenter);
const isValidMaturityDate = (date) => date === null || common_1.typeguards.isFullDateStr(date);
const isWithinLast12Months = (date) => {
    // keep records that have null dates
    if (date === null)
        return true;
    const last12Months = common_1.date.addMonths(common_1.date.currentDateStr(), -12);
    return date >= last12Months;
};
const getCifNumsForFiltering = (vanCust) => {
    const cifNums = new Set();
    vanCust.forEach((cust) => {
        cifNums.add(cust.cif_num);
    });
    return cifNums;
};
const getVanCustAcctRelAccountIdsForFiltering = (vanCustAcctRel) => {
    const vanCustAcctRelAccountIds = new Set();
    vanCustAcctRel.forEach(({ account_id }) => {
        vanCustAcctRelAccountIds.add(account_id);
    });
    return vanCustAcctRelAccountIds;
};
const filterVanCustAcctRel = ({ cifNums, vanCustAcctRel, }) => {
    const logs = [];
    const filtered = [];
    vanCustAcctRel.forEach((acctRel) => {
        if (cifNums.has(acctRel.cif_num)) {
            filtered.push(acctRel);
        }
        else {
            logs.push({
                level: 'info',
                file: 'VanCustAcctRel',
                column: 'account_id',
                identifier: acctRel.account_id,
                message: `Excluded vanCustAcctRel [account_id:${acctRel.account_id}] because its [cif_num:${acctRel.cif_num}] is not associated with any valid vanCust cif_num.`,
            });
        }
    });
    return { logs, filtered };
};
const getVanDeptAcctIbsDepositoryAccountIds = (vanCustAcctRel) => vanCustAcctRel
    .filter((acctRel) => acctRel.app_code === consts_1.DEPOSIT_ACCOUNT_CODE)
    .map(({ account_id }) => account_id);
const filterVanDepAcct = ({ accountIds, vanDepAcct, }) => {
    const logs = [];
    const filtered = [];
    const accountIdSet = new Set(accountIds);
    vanDepAcct.forEach((acct) => {
        if (accountIdSet.has(acct.account_num)) {
            filtered.push(acct);
        }
        else {
            logs.push({
                level: 'info',
                file: 'VanDepAcct',
                column: 'account_id',
                identifier: acct.account_id,
                message: `Excluded vanDepAcct [account_id:${acct.account_id}] [account_num:${acct.account_num}] because its account_num is not associated with a valid vanCustAcctRel record.`,
            });
        }
    });
    return { logs, filtered };
};
const filterLoanCustRelDepositAndCuoDeposit = ({ loanCustRel, cuoDeposit, cifNums, }) => {
    const logs = [];
    const loanCustRelDepAccts = loanCustRel.filter((rel) => rel.heloan_bsys_id === consts_1.OPICS_CODE &&
        rel.helcrel_acct_type === consts_1.DEPOSIT_ACCOUNT_CODE);
    const cuoDepositMap = new Map();
    cuoDeposit.forEach((dep) => {
        cuoDepositMap.set(dep.hedeposit_acct_num, dep);
    });
    const { filteredLoanCustRelDepAcct, filteredCuoDeposit, } = loanCustRelDepAccts.reduce((acc, loanCustRelDepAcct) => {
        const deposit = cuoDepositMap.get(loanCustRelDepAcct.heloan_acct_num);
        if (!deposit) {
            logs.push({
                level: 'info',
                file: 'LoanCustRel',
                column: 'heloan_acct_num',
                identifier: loanCustRelDepAcct.heloan_acct_num,
                message: `Unable to join loanCustRel OPICS deposit account to cuoDeposit for [account_num:${loanCustRelDepAcct.heloan_acct_num}]. This record will be ignored.`,
            });
            return acc;
        }
        if (!deposit.cif_num && !loanCustRelDepAcct.cif_num) {
            logs.push({
                level: 'info',
                file: 'LoanCustRel',
                column: 'heloan_acct_num',
                identifier: loanCustRelDepAcct.heloan_acct_num,
                message: `Unable to identify cif_num for loanCustRel OPICS deposit account [account_num:${loanCustRelDepAcct.heloan_acct_num}].  This record will be ignored.`,
            });
            logs.push({
                level: 'info',
                file: 'CuoDeposit',
                column: 'hedeposit_acct_num',
                identifier: deposit.hedeposit_acct_num,
                message: `Unable to identify cif_num for CuoDeposit record [account_num:${deposit.hedeposit_acct_num}].  This record will be ignored.`,
            });
            return acc;
        }
        // we should still ingest this record, but just log a warning.
        if (deposit.cif_num &&
            loanCustRelDepAcct.cif_num &&
            deposit.cif_num !== loanCustRelDepAcct.cif_num) {
            logs.push({
                level: 'warning',
                file: 'CuoDeposit',
                column: 'hedeposit_acct_num',
                identifier: deposit.hedeposit_acct_num,
                message: `cuoDeposit cif_num is inconsistent with loanCustRel OPICS deposit account cif_num for [account_num:${loanCustRelDepAcct.heloan_acct_num}]`,
            });
        }
        // we prioritize using loanCustRelDepAcct.cif_num if available.
        const cif_num = loanCustRelDepAcct.cif_num ?? deposit.cif_num;
        // this should never occur given logic above
        (0, common_1.finleyAssert)(cif_num, `cif_num must exist in either loanCustRel OPICS deposit account or cuoDeposit for [account_num:${loanCustRelDepAcct.heloan_acct_num}]`);
        if (!cifNums.has(cif_num)) {
            logs.push({
                level: 'info',
                file: 'LoanCustRel',
                column: 'heloan_acct_num',
                identifier: loanCustRelDepAcct.heloan_acct_num,
                message: `loanCustRel OPICS deposit account [heloan_acct_num:${loanCustRelDepAcct.heloan_acct_num}] was ignored (and corresponding CuoDeposit) because it does not have a valid cif_num.`,
            });
        }
        acc.filteredCuoDeposit.push(deposit.cif_num ? deposit : { ...deposit, cif_num });
        acc.filteredLoanCustRelDepAcct.push(loanCustRelDepAcct.cif_num
            ? loanCustRelDepAcct
            : { ...loanCustRelDepAcct, cif_num });
        return acc;
    }, {
        filteredCuoDeposit: [],
        filteredLoanCustRelDepAcct: [],
    });
    return {
        filteredCuoDeposit,
        filteredLoanCustRelDepAcct,
        logs,
    };
};
/**
 * Filter for LoanCustRel + IBS Deposit Accounts
 */
const filterLoanCustRelDepositAndIbsDeposit = ({ loanCustRel, vanDepAcct, }) => {
    const logs = [];
    const loanCustRelDepAccts = loanCustRel.filter((rel) => rel.heloan_bsys_id === consts_1.IBS_CODE &&
        rel.helcrel_acct_type === consts_1.DEPOSIT_ACCOUNT_CODE);
    const vanDepAcctMap = new Map();
    vanDepAcct.forEach((acct) => {
        vanDepAcctMap.set(acct.account_num, acct);
    });
    const { filteredLoanCustRelDepAcct } = loanCustRelDepAccts.reduce((acc, loanCustRelDepAcct) => {
        const deposit = vanDepAcctMap.get(loanCustRelDepAcct.heloan_acct_num);
        // warn if no associated vanDepAcct, but don't filter out loanCustRel record
        if (!deposit) {
            logs.push({
                level: 'info',
                file: 'LoanCustRel',
                column: 'heloan_acct_num',
                identifier: loanCustRelDepAcct.heloan_acct_num,
                message: `Unable to join loanCustRel IBS deposit account to vanDepAcct for [account_num:${loanCustRelDepAcct.heloan_acct_num}].`,
            });
        }
        if (!loanCustRelDepAcct.cif_num) {
            logs.push({
                level: 'info',
                file: 'LoanCustRel',
                column: 'heloan_acct_num',
                identifier: loanCustRelDepAcct.heloan_acct_num,
                message: `Unable to identify cif_num for loanCustRel IBS deposit account [account_num:${loanCustRelDepAcct.heloan_acct_num}].`,
            });
        }
        // we should still ingest this record, but just log a warning.
        if (deposit &&
            deposit.cif_num &&
            loanCustRelDepAcct.cif_num &&
            deposit.cif_num !== loanCustRelDepAcct.cif_num) {
            logs.push({
                level: 'warning',
                file: 'VanDepAcct',
                column: 'account_num',
                identifier: deposit.account_num,
                message: `vanDepAcct cif_num is inconsistent with loanCustRel IBS deposit account cif_num for [account_num:${loanCustRelDepAcct.heloan_acct_num}]`,
            });
        }
        acc.filteredLoanCustRelDepAcct.push(loanCustRelDepAcct);
        return acc;
    }, {
        filteredLoanCustRelDepAcct: [],
    });
    return {
        filteredLoanCustRelDepAcct,
        logs,
    };
};
const filterCuoCollatAndCollatCustRelTapes = ({ cuoCollat, collatCustRel, filteredCuoDeposit, filteredVanDepAcct, }) => {
    const logs = [];
    const cuoCollatAccountNumMap = new Map();
    cuoCollat.forEach((collat) => {
        if (collat.hecoll_deposit_account_num) {
            cuoCollatAccountNumMap.set(collat.hecoll_deposit_account_num, collat);
        }
        else {
            logs.push({
                level: 'info',
                file: 'CuoCollat',
                column: 'hecoll_id',
                identifier: collat.hecoll_id,
                message: `cuoCollat [hecoll_id:${collat.hecoll_id}] hecoll_deposit_account_num is null and cannot be associated with collateral account. This record will be ignored.`,
            });
        }
    });
    const filteredCuoCollat = [];
    filteredCuoDeposit.forEach((deposit) => {
        const cuoDep = cuoCollatAccountNumMap.get(deposit.hedeposit_acct_num);
        if (cuoDep) {
            filteredCuoCollat.push(cuoDep);
        }
    });
    filteredVanDepAcct.forEach((acct) => {
        const cuoDep = cuoCollatAccountNumMap.get(acct.account_id);
        if (cuoDep) {
            filteredCuoCollat.push(cuoDep);
        }
    });
    const collatCustRelHecollIdMap = new Map();
    collatCustRel.forEach((rel) => {
        collatCustRelHecollIdMap.set(rel.he_coll_id, rel);
    });
    const filteredCollatCustRel = [];
    filteredCuoCollat.forEach((collat) => {
        const rel = collatCustRelHecollIdMap.get(collat.hecoll_id);
        if (rel) {
            filteredCollatCustRel.push(rel);
        }
        else {
            logs.push({
                level: 'error',
                file: 'CuoCollat',
                column: 'hecoll_id',
                identifier: collat.hecoll_id,
                message: `Unable to join CuoCollat to CollatCustRel for [hecoll_id:${collat.hecoll_id}].`,
            });
        }
    });
    return {
        logs,
        filteredCollatCustRel,
        filteredCuoCollat,
    };
};
const fitlerPsPlusPortfolio = ({ psPlusPortfolio, cifNums, }) => {
    const logs = [];
    const filtered = [];
    psPlusPortfolio.forEach((port) => {
        if (cifNums.has(port.cif_num)) {
            filtered.push(port);
        }
        else {
            logs.push({
                level: 'info',
                file: 'PsPlusPortfolio',
                column: 'hecoll_id',
                identifier: port.hecoll_id,
                message: `Excluded PsPlusPortfolio [heccrel_portfolio_number:${port.heccrel_portfolio_number}] [hecoll_id:${port.hecoll_id}] because [cif_num:${port.cif_num}] is not associated with any valid cif_num.`,
            });
        }
    });
    return { logs, filtered };
};
const filterPsPlusAccount = ({ psPlusAccount, filteredPsPlusPortfolio, }) => {
    const logs = [];
    const filtered = [];
    const heCollIdSet = new Set(filteredPsPlusPortfolio.map(({ hecoll_id }) => hecoll_id));
    psPlusAccount.forEach((account) => {
        if (heCollIdSet.has(account.hecoll_id)) {
            filtered.push(account);
        }
        else {
            logs.push({
                level: 'info',
                file: 'PsPlusAccount',
                column: 'hecoll_id',
                identifier: account.hecoll_id,
                message: `Excluded PsPlusAccount [hecoll_id:${account.hecoll_id}] because hecoll_id is not associated with any valid psPlusPortfolio hecoll_id.`,
            });
        }
    });
    return {
        logs,
        filtered,
    };
};
exports.filterPsPlusAccount = filterPsPlusAccount;
const filterPsPlusSecDetail = ({ filteredPsPlusAccount, psPlusSecDetail, psPlusSecMaster, }) => {
    const logs = [];
    const filtered = [];
    const heCollIdSet = new Set(filteredPsPlusAccount.map(({ hecoll_id }) => hecoll_id));
    const securityNumberSet = new Set(psPlusSecMaster.map(({ he_sec_security_num }) => he_sec_security_num));
    psPlusSecDetail.forEach((detail) => {
        if (heCollIdSet.has(detail.hecoll_collateral_account_number)) {
            filtered.push(detail);
        }
        else {
            logs.push({
                level: 'info',
                file: 'PsPlusSecDetail',
                column: 'hecoll_collateral_account_number',
                identifier: detail.hecoll_collateral_account_number,
                message: `Excluded PsPlusSecDetail [hecoll_collateral_account_number:${detail.hecoll_collateral_account_number}] [he_sec_security_num: ${detail.he_sec_security_num}] because hecoll_collateral_account_number is not associated with any valid psPlusAccount.`,
            });
        }
        if (!securityNumberSet.has(detail.he_sec_security_num)) {
            logs.push({
                level: 'error',
                file: 'PsPlusSecDetail',
                column: 'hecoll_collateral_account_number',
                identifier: detail.hecoll_collateral_account_number,
                message: `Unable to join PsPlusSecDetail [hecoll_collateral_account_number:${detail.hecoll_collateral_account_number}] [he_sec_security_num: ${detail.he_sec_security_num}] to psPlusSecMaster.`,
            });
        }
    });
    return {
        logs,
        filtered,
    };
};
exports.filterPsPlusSecDetail = filterPsPlusSecDetail;
const filterVanCmmt = ({ vanCmmt, validVanCustAcctRelAcctIds, }) => {
    const logs = [];
    const filtered = [];
    vanCmmt.forEach((cmmt) => {
        if (validVanCustAcctRelAcctIds.has(cmmt.account_num)) {
            if (!isValidMaturityDate(cmmt.expire_date)) {
                logs.push({
                    level: 'info',
                    file: 'VanCmmt',
                    column: 'commitment_id',
                    identifier: cmmt.commitment_id,
                    message: `Excluded vanCmmt [commitment_id:${cmmt.commitment_id}] [account_num:${cmmt.account_num}] because its [expire_date: ${cmmt.expire_date}] is not null nor formatted as a date string [yyyy-mm-dd]`,
                });
            }
            if (isValidMaturityDate(cmmt.expire_date) &&
                isWithinLast12Months(cmmt.expire_date) &&
                typeof cmmt.cost_center === 'string' &&
                isPrivateBankingCostCenter(cmmt.cost_center) &&
                cmmt.active_status !== 'P' && // ignore P active status
                cmmt.type !== 'SWP' // ignore SWP
            ) {
                filtered.push(cmmt);
            }
        }
        else {
            logs.push({
                level: 'info',
                file: 'VanCmmt',
                column: 'commitment_id',
                identifier: cmmt.commitment_id,
                message: `Excluded vanCmmt [commitment_id:${cmmt.commitment_id}] [account_num:${cmmt.account_num}] because its accountNum is not associated with any valid vanCustAcctRel account_id.`,
            });
        }
    });
    return {
        logs,
        filtered,
    };
};
exports.filterVanCmmt = filterVanCmmt;
const filterVanLoanAcct = ({ vanLoanAcct, validVanCustAcctRelAcctIds, }) => {
    const logs = [];
    const filtered = [];
    const ignoredStatusSubCodes = new Set(['P', 'C', 'T', 'Z']);
    vanLoanAcct.forEach((loan) => {
        if (validVanCustAcctRelAcctIds.has(loan.account_num)) {
            if (!isValidMaturityDate(loan.maturity_date)) {
                logs.push({
                    level: 'info',
                    file: 'VanLoanAcct',
                    column: 'loan_id',
                    identifier: loan.loan_id,
                    message: `Excluded VanLoanAcct [loan_id:${loan.loan_id}] [account_num:${loan.account_num}] because its [maturity_date: ${loan.maturity_date}] is not null nor formatted as a date string [yyyy-mm-dd]`,
                });
            }
            if (isValidMaturityDate(loan.maturity_date) &&
                isWithinLast12Months(loan.maturity_date) &&
                isPrivateBankingCostCenter(loan.cost_center) &&
                !ignoredStatusSubCodes.has(loan.loan_status_sub_code)) {
                filtered.push(loan);
            }
        }
        else {
            logs.push({
                level: 'info',
                file: 'VanLoanAcct',
                column: 'loan_id',
                identifier: loan.loan_id,
                message: `Excluded VanLoanAcct [loanId:${loan.loan_id}] because its accountNum is not associated with any valid vanCustAcctRel account_id.`,
            });
        }
    });
    return {
        logs,
        filtered,
    };
};
exports.filterVanLoanAcct = filterVanLoanAcct;
const filterLoanCustRelLoanAcct = ({ loanCustRel, filteredVanCmmt, filteredVanLoanAcct, }) => {
    const filtered = [];
    const loanAccountNums = new Set();
    filteredVanCmmt.forEach((cmmt) => {
        loanAccountNums.add(cmmt.account_num);
    });
    filteredVanLoanAcct.forEach((acct) => {
        loanAccountNums.add(acct.account_num);
    });
    loanCustRel.forEach((rel) => {
        if (loanAccountNums.has(rel.heloan_acct_num)) {
            filtered.push(rel);
        }
    });
    return { filtered };
};
const filterCuoLoan = ({ cuoLoan, filteredVanCmmt, cifNums, }) => {
    const logs = [];
    const filtered = [];
    const vanCmmtIds = new Set(filteredVanCmmt.map(({ commitment_id }) => commitment_id));
    const nonCLcuoLoan = cuoLoan.filter((loan) => loan.heloan_prod_type !== 'CL');
    nonCLcuoLoan.forEach((loan) => {
        if (vanCmmtIds.has(loan.heloan_parent_acct_num ?? '') ||
            cifNums.has(loan.cif_num ?? '') ||
            !!loan.heloan_universal_num) {
            if (!loan.heloan_universal_num) {
                logs.push({
                    level: 'warning',
                    file: 'CuoLoan',
                    column: 'heloan_acct_num',
                    identifier: loan.heloan_acct_num,
                    message: `CuoLoan [heloan_acct_num:${loan.heloan_acct_num}] has a cif_num or heloan_parent_acct_num, but is missing heloan_universal_num.`,
                });
            }
            if (!isValidMaturityDate(loan.heloan_mat_dt)) {
                logs.push({
                    level: 'warning',
                    file: 'CuoLoan',
                    column: 'heloan_acct_num',
                    identifier: loan.heloan_acct_num,
                    message: `Excluded CuoLoan [heloan_acct_num:${loan.heloan_acct_num}] because its [heloan_mat_dt: ${loan.heloan_mat_dt}] is not null nor formatted as a date string [yyyy-mm-dd]`,
                });
            }
            if (isValidMaturityDate(loan.heloan_mat_dt) &&
                isWithinLast12Months(loan.heloan_mat_dt)) {
                filtered.push(loan);
            }
        }
        else {
            logs.push({
                level: 'info',
                file: 'CuoLoan',
                column: 'heloan_acct_num',
                identifier: loan.heloan_acct_num,
                message: `Excluded CuoLoan [heloan_acct_num:${loan.heloan_acct_num}] because its heloan_parent_acct_num is not associated with any valid vanCmmt commitment_id or valid vanCust cif_num`,
            });
        }
    });
    return {
        logs,
        filtered,
    };
};
exports.filterCuoLoan = filterCuoLoan;
const filterCuoMaintAndUpdateVanCmmt = ({ cuoMaint, filteredVanCmmt, }) => {
    const logs = [];
    const filtered = [];
    const vanCmmtMap = new Map();
    filteredVanCmmt.forEach((cmmt) => {
        vanCmmtMap.set(cmmt.commitment_id, { ...cmmt }); // making copy as we will mutate vanCmmt used amount
    });
    cuoMaint.forEach((maint) => {
        const cmmt = vanCmmtMap.get(maint.heloan_acct_num);
        if (cmmt) {
            filtered.push(maint);
            cmmt.used_amt = maint.heloan_collat_analysis_bal;
        }
        else {
            logs.push({
                level: 'info',
                file: 'CuoMaint',
                column: 'heloan_acct_num',
                identifier: maint.heloan_acct_num,
                message: `Excluded CuoMaint [heloan_acct_num:${maint.heloan_acct_num}] because its heloan_acct_num is not associated with any valid VanCmmt commitment_id.`,
            });
        }
    });
    return {
        logs,
        filtered,
        updatedVanCmmt: Array.from(vanCmmtMap.values()),
    };
};
exports.filterCuoMaintAndUpdateVanCmmt = filterCuoMaintAndUpdateVanCmmt;
const filterDerivativePath = ({ derivativePath, cifNums, }) => {
    const logs = [];
    const filtered = [];
    derivativePath.forEach((derivPath) => {
        if (cifNums.has(derivPath.cif_num)) {
            filtered.push(derivPath);
        }
        else {
            logs.push({
                level: 'warning',
                file: 'DerivativePath',
                column: 'cif_num',
                identifier: derivPath.id,
                message: `Excluded DerivativePath [id:${derivPath.id}] [account_num:${derivPath.account_num}] because [cif_num:${derivPath.cif_num}] is not associated with any valid cif_num.`,
            });
        }
    });
    return { logs, filtered };
};
const filterFinleyValleyBankTapes = ({ vanCust, vanDepAcct, vanCustAcctRel, loanCustRel, cuoDeposit, cuoCollat, collatCustRel, psPlusPortfolio, psPlusAccount, psPlusSecDetail, psPlusSecMaster, vanCmmt, vanLoanAcct, cuoLoan, vanList, vanProduct, cuoMaint, derivativePath, }) => {
    const logs = [];
    const cifNums = getCifNumsForFiltering(vanCust);
    const { filtered: filteredVanCustAcctRel, logs: filteredVanCustAcctRelLogs, } = filterVanCustAcctRel({
        vanCustAcctRel,
        cifNums,
    });
    filteredVanCustAcctRelLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredVanDepAcct, logs: filteredVanDepAccoutLogs, } = filterVanDepAcct({
        vanDepAcct,
        accountIds: getVanDeptAcctIbsDepositoryAccountIds(filteredVanCustAcctRel),
    });
    filteredVanDepAccoutLogs.forEach((log) => {
        logs.push(log);
    });
    const { filteredCuoDeposit, filteredLoanCustRelDepAcct: filteredLoanCustRelDepAcctOpics, logs: cuoDepositLogs, } = filterLoanCustRelDepositAndCuoDeposit({
        loanCustRel,
        cuoDeposit,
        cifNums,
    });
    cuoDepositLogs.forEach((log) => {
        logs.push(log);
    });
    const { filteredLoanCustRelDepAcct: filteredLoanCustRelDepAcctIbs, logs: loanCustRelDeptAcctIbsLogs, } = filterLoanCustRelDepositAndIbsDeposit({
        loanCustRel,
        vanDepAcct: filteredVanDepAcct,
    });
    loanCustRelDeptAcctIbsLogs.forEach((log) => {
        logs.push(log);
    });
    const { filteredCollatCustRel, filteredCuoCollat, logs: cuoCollatLogs, } = filterCuoCollatAndCollatCustRelTapes({
        collatCustRel,
        cuoCollat,
        filteredCuoDeposit,
        filteredVanDepAcct,
    });
    cuoCollatLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredPsPlusPortfolio, logs: psPlusPortfolioLogs, } = fitlerPsPlusPortfolio({
        psPlusPortfolio,
        cifNums,
    });
    psPlusPortfolioLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredPsPlusAccount, logs: psPlusAccountLogs, } = (0, exports.filterPsPlusAccount)({
        filteredPsPlusPortfolio,
        psPlusAccount,
    });
    psPlusAccountLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredPsPlusSecDetail, logs: psPlusSecDetailLogs, } = (0, exports.filterPsPlusSecDetail)({
        filteredPsPlusAccount,
        psPlusSecDetail,
        psPlusSecMaster,
    });
    psPlusSecDetailLogs.forEach((log) => {
        logs.push(log);
    });
    const validVanCustAcctRelAcctIds = getVanCustAcctRelAccountIdsForFiltering(filteredVanCustAcctRel);
    const { filtered: filteredVanCmmt, logs: vanCmmtLogs } = (0, exports.filterVanCmmt)({
        vanCmmt,
        validVanCustAcctRelAcctIds,
    });
    vanCmmtLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredVanLoanAcct, logs: vanLoanAcctLogs, } = (0, exports.filterVanLoanAcct)({
        vanLoanAcct,
        validVanCustAcctRelAcctIds,
    });
    vanLoanAcctLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredLoanCustRelLoanAcct } = filterLoanCustRelLoanAcct({
        loanCustRel,
        filteredVanLoanAcct,
        filteredVanCmmt,
    });
    const filteredLoanCustRel = [];
    filteredLoanCustRelDepAcctIbs.forEach((acct) => {
        filteredLoanCustRel.push(acct);
    });
    filteredLoanCustRelDepAcctOpics.forEach((acct) => {
        filteredLoanCustRel.push(acct);
    });
    filteredLoanCustRelLoanAcct.forEach((acct) => {
        filteredLoanCustRel.push(acct);
    });
    const { filtered: filteredCuoLoan, logs: cuoLoanLogs } = (0, exports.filterCuoLoan)({
        cuoLoan,
        filteredVanCmmt,
        cifNums,
    });
    cuoLoanLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredCuoMaint, logs: cuoMaintLogs, updatedVanCmmt, } = (0, exports.filterCuoMaintAndUpdateVanCmmt)({
        cuoMaint,
        filteredVanCmmt,
    });
    cuoMaintLogs.forEach((log) => {
        logs.push(log);
    });
    const { filtered: filteredDerivativePath, logs: derivativePathLogs, } = filterDerivativePath({
        derivativePath,
        cifNums,
    });
    derivativePathLogs.forEach((log) => {
        logs.push(log);
    });
    return {
        filteredFinleyValleyBankTapes: {
            vanCust, // we ingest all vanCust
            vanDepAcct: filteredVanDepAcct,
            vanCustAcctRel: filteredVanCustAcctRel,
            loanCustRel: filteredLoanCustRel,
            cuoDeposit: filteredCuoDeposit,
            collatCustRel: filteredCollatCustRel,
            cuoCollat: filteredCuoCollat,
            psPlusPortfolio: filteredPsPlusPortfolio,
            psPlusAccount: filteredPsPlusAccount,
            psPlusSecDetail: filteredPsPlusSecDetail,
            psPlusSecMaster, // we don't need to filter psPlusSecMaster
            vanCmmt: updatedVanCmmt,
            vanLoanAcct: filteredVanLoanAcct,
            vanProduct, // static mapping, no filter
            vanList, // static mapping, no filter
            cuoLoan: filteredCuoLoan,
            cuoMaint: filteredCuoMaint,
            derivativePath: filteredDerivativePath,
        },
        logs,
    };
};
exports.filterFinleyValleyBankTapes = filterFinleyValleyBankTapes;
//# sourceMappingURL=filter.js.map